import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportReactError } from "@/lib/debugCapture";

type Props = {
  children: ReactNode;
  /** Optional fallback UI. Defaults to a minimal inline message. */
  fallback?: ReactNode;
};

type State = { hasError: boolean };

/**
 * Catches render-time errors anywhere below it in the tree, ships the error
 * (with React component stack) to the admin error queue, and shows a quiet
 * fallback so the rest of the page keeps working.
 *
 * Note: ErrorBoundary only catches *render* errors — async/event-handler
 * errors are caught by the global window.error / unhandledrejection
 * listeners installed in `installConsoleCapture`.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportReactError(error, info.componentStack || "");
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="min-h-[200px] flex items-center justify-center p-6">
            <p className="text-sm font-mono text-muted-foreground">
              Something went wrong rendering this section. The team has been notified.
            </p>
          </div>
        )
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
